import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Automation } from "../api/useAutomations";

export type AutomationType = "event-based" | "scheduled";
export type ScheduledEventType = "report" | "notification" | "alert" | null;
export type ReportRecipientType = "user" | "role" | "other" | null;
/** Scheduled report source: an existing saved report view, or a new one-off
 *  definition (tab + sections). */
export type ReportSourceType = "view" | "new" | "favorite";
export type ReportExportFormat = "csv" | "xlsx";
export type AutomationScopeForm = "GLOBAL" | "SPECIFIC";
export type AutomationFrequencyForm = "EVERY_TIME" | "ONCE" | "ONCE_IN_PERIOD";

/** One WhatsApp template variable of a scheduled report's WhatsApp delivery. */
export interface ReportWhatsappParamForm {
  paramName: string;
  source: "report_value" | "static";
  value: string;
}
export type RunWindowModeForm =
  | "ALL"
  | "BUSINESS_HOURS"
  | "OUTSIDE_BUSINESS_HOURS";

// Default business-hours window: 9:30am–5:30pm IST, all 7 days enabled.
// (570 = 9*60+30, 1050 = 17*60+30.) Days: 0=Sun … 6=Sat.
export const DEFAULT_RUN_WINDOW_START = 570;
export const DEFAULT_RUN_WINDOW_END = 1050;
export const DEFAULT_RUN_WINDOW_DAYS = [0, 1, 2, 3, 4, 5, 6];

// Legacy flat-condition shape; still used by reportFilters and the standalone
// AutomationRuleBuilder. Kept intentionally for backward compatibility.
export interface AutomationCondition {
  id: string;
  field: string;
  operator: string;
  value: string;
}

// ── Condition tree (matches server ConditionNode / ConditionGroup) ──

export type ConditionCombinator = "AND" | "OR";

export interface ConditionLeafForm {
  id: string;
  kind: "leaf";
  field: string;
  operator: string;
  value: string;
  /** Text patterns (Starts with / Contains / …) only: compare upper / lower case exactly. */
  matchCase?: boolean;
}

export interface ConditionGroupForm {
  id: string;
  kind: "group";
  combinator: ConditionCombinator;
  children: ConditionNodeForm[];
}

export type ConditionNodeForm = ConditionLeafForm | ConditionGroupForm;

export interface AutomationActionForm {
  id: string;
  actionType: string;
  config: Record<string, any>;
}

export interface AutomationState {
  isDeleteModal: boolean;
  /** Header quick-search on /automation — filters My Automations client-side. */
  automationSearch: string;
  isCollapse: boolean;
  currentItem: Automation | null;
  currentItemTab: "overview" | "Logs";
  isAddAutomation: boolean;
  isEditAutomation: boolean;
  editAutomationId: number | null;
  isDetailDrawerOpen: boolean;
  isLogDetailDrawerOpen: boolean;
  selectedLog: any | null;
  // Rolled-up sweep drill-in: the selected log GROUP (batch summary) + its drawer.
  isBatchDrawerOpen: boolean;
  selectedBatch: any | null;
  automationType: AutomationType;
  automationName: string;
  automationDescription: string;
  automationTrigger: string | null;
  automationTriggerConfig: Record<string, any>;
  automationConditionTree: ConditionGroupForm;
  automationActions: AutomationActionForm[];
  automationScope: AutomationScopeForm;
  automationGroupId: number | null;
  automationFrequency: AutomationFrequencyForm;
  automationPeriodSeconds: number;
  runWindowMode: RunWindowModeForm;
  runWindowDays: number[];
  runWindowStart: number; // IST minutes from midnight (e.g. 570 = 09:30)
  runWindowEnd: number; // IST minutes from midnight (e.g. 1050 = 17:30)
  stopOnFailure: boolean;
  skipIfAlreadyApplied: boolean;
  automationPriority: number;
  branchId: number | null;
  scheduledEventType: ScheduledEventType;
  cronExpression: string;
  reportMetric: string;
  reportGroupBy: string;
  reportFilters: AutomationCondition[];
  // Scheduled-report definition (serialized into automation.reportConfig).
  reportSource: ReportSourceType;
  /** saved report view id (reportSource === "view") */
  reportViewId: string;
  /** favorite (saved leads) view id (reportSource === "favorite") — the only
   *  source the editor currently offers; the run delivers its TOTAL as text */
  reportFavoriteViewId: string;
  /** reports-v2 tab key (reportSource === "new"; also mirrors the view's tab) */
  reportTabKey: string;
  reportFormat: ReportExportFormat;
  /** reports-v2 timeframe key (never "custom" — schedules need a rolling window) */
  reportTimeframe: string;
  /** export-registry section keys to include (empty = all for the tab) */
  reportSections: string[];
  reportRecipientType: ReportRecipientType;
  reportRecipientId: string;
  reportAdditionalEmail: string;
  reportNotes: string;
  // Scheduled NOTIFICATION definition (serialized into the same
  // automation.reportConfig column — see buildNotificationConfig).
  /** "none" = plain reminder; a metric key prepends its live count. */
  notificationContext: string;
  notificationMessage: string;
  /** Email channel for scheduled reports — off = WhatsApp-only schedule. */
  reportEmailEnabled: boolean;
  /**
   * SHARE-LINK channel (reportConfig.shareLink): each run mints a link to the
   * chosen favourite view's live list and sends it to the recipients (inbox
   * card + email). Carries no numbers and no file — the recipient opens the
   * list under their own access.
   */
  reportShareLinkEnabled: boolean;
  /** Note that rides the share email ("" = the schedule names itself). */
  reportShareLinkNote: string;
  // Scheduled-report WhatsApp delivery (reportConfig.whatsapp).
  reportWhatsappEnabled: boolean;
  /** `"WATI:12"` — provider + LeadSource id of the picked BSP account. */
  reportWhatsappSource: string;
  reportWhatsappTemplateName: string;
  reportWhatsappTemplateLanguage: string;
  reportWhatsappParams: ReportWhatsappParamForm[];
  /** Comma/newline-separated literal numbers typed into the editor. */
  reportWhatsappPhones: string;
  reportWhatsappSendToRecipients: boolean;
  // Scheduled ALERT definition (buildAlertConfig).
  alertMetric: string;
  alertOperator: string;
  /** Kept as the raw input string; parsed to a number at save time. */
  alertThreshold: string;
  alertMessage: string;
}

// ── Tree helpers ──

const newLeaf = (): ConditionLeafForm => ({
  id: crypto.randomUUID(),
  kind: "leaf",
  field: "",
  operator: "",
  value: "",
});

const newGroup = (
  combinator: ConditionCombinator = "AND",
  children: ConditionNodeForm[] = [],
): ConditionGroupForm => ({
  id: crypto.randomUUID(),
  kind: "group",
  combinator,
  children,
});

const initialConditionTree = (): ConditionGroupForm => newGroup("AND", [newLeaf()]);

// Locate a node by id (returns the node + its parent group). The root group has no parent.
const findNode = (
  root: ConditionGroupForm,
  id: string,
): { node: ConditionNodeForm; parent: ConditionGroupForm | null } | null => {
  if (root.id === id) return { node: root, parent: null };
  const stack: { node: ConditionGroupForm; parent: ConditionGroupForm | null }[] = [
    { node: root, parent: null },
  ];
  while (stack.length) {
    const { node: group } = stack.pop()!;
    for (const child of group.children) {
      if (child.id === id) return { node: child, parent: group };
      if (child.kind === "group") stack.push({ node: child, parent: group });
    }
  }
  return null;
};

const findGroup = (
  root: ConditionGroupForm,
  id: string,
): ConditionGroupForm | null => {
  const found = findNode(root, id);
  if (found && found.node.kind === "group") return found.node;
  return null;
};

const findLeaf = (
  root: ConditionGroupForm,
  id: string,
): ConditionLeafForm | null => {
  const found = findNode(root, id);
  if (found && found.node.kind === "leaf") return found.node;
  return null;
};

// ── Parse server payload into tree (tolerates legacy flat array) ──

const parseLeaf = (raw: any): ConditionLeafForm => ({
  id: crypto.randomUUID(),
  kind: "leaf",
  field: raw?.field || "",
  operator: raw?.operator || "",
  ...(raw?.matchCase === true ? { matchCase: true } : {}),
  value:
    raw?.value === undefined || raw?.value === null
      ? ""
      : typeof raw.value === "string"
        ? raw.value
        : Array.isArray(raw.value)
          ? raw.value.join(",")
          : String(raw.value),
});

const parseNode = (raw: any): ConditionNodeForm => {
  if (raw && (Array.isArray(raw.AND) || Array.isArray(raw.OR))) {
    const combinator: ConditionCombinator = Array.isArray(raw.AND) ? "AND" : "OR";
    const children: any[] = combinator === "AND" ? raw.AND : raw.OR;
    return {
      id: crypto.randomUUID(),
      kind: "group",
      combinator,
      children: (children || []).map(parseNode),
    };
  }
  return parseLeaf(raw);
};

export const parseConditionPayload = (payload: any): ConditionGroupForm => {
  if (!payload) return initialConditionTree();

  if (Array.isArray(payload)) {
    if (payload.length === 0) return initialConditionTree();
    return newGroup("AND", payload.map(parseNode));
  }

  if (typeof payload === "object") {
    if (Array.isArray(payload.AND)) {
      return newGroup("AND", payload.AND.map(parseNode));
    }
    if (Array.isArray(payload.OR)) {
      return newGroup("OR", payload.OR.map(parseNode));
    }
    if (payload.field && payload.operator) {
      return newGroup("AND", [parseLeaf(payload)]);
    }
  }

  return initialConditionTree();
};

// ── Serialize tree back to server-shaped payload ──

const NO_VALUE_OPERATORS = new Set(["is_empty", "is_not_empty"]);
/** Operators that compare letters; only these carry `matchCase` (server: name-pattern-fields.ts). */
export const TEXT_PATTERN_OPERATORS = new Set([
  "contains",
  "not_contains",
  "starts_with",
  "not_starts_with",
  "ends_with",
]);

const serializeLeaf = (leaf: ConditionLeafForm) => {
  if (!leaf.field || !leaf.operator) return null;
  const noValue = NO_VALUE_OPERATORS.has(leaf.operator);
  if (!noValue && (leaf.value === undefined || leaf.value === "")) return null;
  return {
    field: leaf.field,
    operator: leaf.operator,
    ...(noValue ? {} : { value: leaf.value }),
    ...(leaf.matchCase && TEXT_PATTERN_OPERATORS.has(leaf.operator) ? { matchCase: true } : {}),
  };
};

const serializeGroup = (group: ConditionGroupForm): any | null => {
  const children = group.children
    .map((c) =>
      c.kind === "leaf" ? serializeLeaf(c) : serializeGroup(c as ConditionGroupForm),
    )
    .filter((c): c is any => c !== null);
  if (children.length === 0) return null;
  if (children.length === 1) return children[0];
  return group.combinator === "AND" ? { AND: children } : { OR: children };
};

export const serializeConditionTree = (
  root: ConditionGroupForm,
): any[] | Record<string, any> | undefined => {
  const result = serializeGroup(root);
  if (!result) return undefined;
  // Group object → return as-is.
  if (result && typeof result === "object" && (Array.isArray(result.AND) || Array.isArray(result.OR))) {
    return result;
  }
  // Single leaf at root → wrap in implicit-AND array (server accepts both shapes).
  return [result];
};

const initialState: AutomationState = {
  isDeleteModal: false,
  automationSearch: "",
  isCollapse: false,
  isAddAutomation: false,
  isEditAutomation: false,
  editAutomationId: null,
  isDetailDrawerOpen: false,
  isLogDetailDrawerOpen: false,
  selectedLog: null,
  isBatchDrawerOpen: false,
  selectedBatch: null,
  currentItem: null,
  currentItemTab: "overview",
  automationType: "event-based",
  automationName: "",
  automationDescription: "",
  automationTrigger: null,
  automationTriggerConfig: {},
  automationConditionTree: initialConditionTree(),
  automationActions: [{ id: "1", actionType: "", config: {} }],
  automationScope: "GLOBAL",
  automationGroupId: null,
  automationFrequency: "EVERY_TIME",
  automationPeriodSeconds: 0,
  runWindowMode: "ALL",
  runWindowDays: DEFAULT_RUN_WINDOW_DAYS,
  runWindowStart: DEFAULT_RUN_WINDOW_START,
  runWindowEnd: DEFAULT_RUN_WINDOW_END,
  stopOnFailure: true,
  skipIfAlreadyApplied: true,
  automationPriority: 10,
  branchId: null,
  scheduledEventType: "report",
  cronExpression: "",
  reportMetric: "",
  reportGroupBy: "",
  reportFilters: [{ id: "1", field: "", operator: "", value: "" }],
  reportSource: "favorite",
  reportViewId: "",
  reportFavoriteViewId: "",
  reportTabKey: "lead",
  reportFormat: "csv",
  reportTimeframe: "this_month",
  reportSections: [],
  reportRecipientType: "user",
  reportRecipientId: "",
  reportAdditionalEmail: "",
  reportNotes: "",
  notificationContext: "none",
  notificationMessage: "",
  reportEmailEnabled: true,
  reportShareLinkEnabled: false,
  reportShareLinkNote: "",
  reportWhatsappEnabled: false,
  reportWhatsappSource: "",
  reportWhatsappTemplateName: "",
  reportWhatsappTemplateLanguage: "",
  reportWhatsappParams: [],
  reportWhatsappPhones: "",
  reportWhatsappSendToRecipients: true,
  alertMetric: "overdue_followups",
  alertOperator: "gt",
  alertThreshold: "",
  alertMessage: "",
};

const automationFunctionalSlice = createSlice({
  name: "automationFunctional",
  initialState,
  reducers: {
    resetAutomationForm(state) {
      state.automationType = initialState.automationType;
      state.automationName = initialState.automationName;
      state.automationDescription = initialState.automationDescription;
      state.automationTrigger = initialState.automationTrigger;
      state.automationTriggerConfig = initialState.automationTriggerConfig;
      state.automationConditionTree = initialConditionTree();
      state.automationActions = [
        { id: crypto.randomUUID(), actionType: "", config: {} },
      ];
      state.automationScope = initialState.automationScope;
      state.automationGroupId = initialState.automationGroupId;
      state.automationFrequency = initialState.automationFrequency;
      state.automationPeriodSeconds = initialState.automationPeriodSeconds;
      state.runWindowMode = initialState.runWindowMode;
      state.runWindowDays = initialState.runWindowDays;
      state.runWindowStart = initialState.runWindowStart;
      state.runWindowEnd = initialState.runWindowEnd;
      state.stopOnFailure = initialState.stopOnFailure;
      state.skipIfAlreadyApplied = initialState.skipIfAlreadyApplied;
      state.automationPriority = initialState.automationPriority;
      state.branchId = initialState.branchId;
      state.scheduledEventType = initialState.scheduledEventType;
      state.cronExpression = initialState.cronExpression;
      state.reportSource = initialState.reportSource;
      state.reportViewId = initialState.reportViewId;
      state.reportFavoriteViewId = initialState.reportFavoriteViewId;
      state.reportTabKey = initialState.reportTabKey;
      state.reportFormat = initialState.reportFormat;
      state.reportTimeframe = initialState.reportTimeframe;
      state.reportSections = [];
      state.reportRecipientType = initialState.reportRecipientType;
      state.reportRecipientId = initialState.reportRecipientId;
      state.reportAdditionalEmail = initialState.reportAdditionalEmail;
      state.reportNotes = initialState.reportNotes;
      state.notificationContext = initialState.notificationContext;
      state.notificationMessage = initialState.notificationMessage;
      state.reportEmailEnabled = initialState.reportEmailEnabled;
      state.reportShareLinkEnabled = initialState.reportShareLinkEnabled;
      state.reportShareLinkNote = initialState.reportShareLinkNote;
      state.reportWhatsappEnabled = initialState.reportWhatsappEnabled;
      state.reportWhatsappSource = initialState.reportWhatsappSource;
      state.reportWhatsappTemplateName = initialState.reportWhatsappTemplateName;
      state.reportWhatsappTemplateLanguage =
        initialState.reportWhatsappTemplateLanguage;
      state.reportWhatsappParams = [];
      state.reportWhatsappPhones = initialState.reportWhatsappPhones;
      state.reportWhatsappSendToRecipients =
        initialState.reportWhatsappSendToRecipients;
      state.alertMetric = initialState.alertMetric;
      state.alertOperator = initialState.alertOperator;
      state.alertThreshold = initialState.alertThreshold;
      state.alertMessage = initialState.alertMessage;
      state.editAutomationId = null;
      state.isEditAutomation = false;
    },
    populateFormFromAutomation(state, action: PayloadAction<Automation>) {
      const a = action.payload;
      state.editAutomationId = a.id;
      state.isEditAutomation = true;
      state.automationType =
        a.type === "SCHEDULED" ? "scheduled" : "event-based";
      state.automationName = a.name;
      state.automationDescription = a.description || "";
      state.automationScope = a.scope || "GLOBAL";
      state.automationGroupId = a.groupId ?? null;
      state.automationFrequency =
        (a.frequency as AutomationFrequencyForm) || "EVERY_TIME";
      state.automationPeriodSeconds = a.periodSeconds || 0;
      // Active-hours window. Older automations have no runWindow → "ALL".
      const rw = a.runWindow;
      if (
        rw &&
        (rw.mode === "BUSINESS_HOURS" || rw.mode === "OUTSIDE_BUSINESS_HOURS")
      ) {
        state.runWindowMode = rw.mode;
        state.runWindowDays =
          Array.isArray(rw.days) && rw.days.length
            ? rw.days
            : DEFAULT_RUN_WINDOW_DAYS;
        state.runWindowStart =
          typeof rw.startMinute === "number"
            ? rw.startMinute
            : DEFAULT_RUN_WINDOW_START;
        state.runWindowEnd =
          typeof rw.endMinute === "number"
            ? rw.endMinute
            : DEFAULT_RUN_WINDOW_END;
      } else {
        state.runWindowMode = "ALL";
        state.runWindowDays = DEFAULT_RUN_WINDOW_DAYS;
        state.runWindowStart = DEFAULT_RUN_WINDOW_START;
        state.runWindowEnd = DEFAULT_RUN_WINDOW_END;
      }
      state.stopOnFailure = a.stopOnFailure ?? true;
      state.skipIfAlreadyApplied = a.skipIfAlreadyApplied ?? true;
      state.automationPriority = a.priority ?? 10;
      state.branchId = a.branchId ?? null;
      state.automationTrigger = a.trigger?.event || null;
      state.automationTriggerConfig = a.trigger?.config || {};
      state.cronExpression = a.cronExpression || "";
      state.scheduledEventType =
        (a.scheduledType?.toLowerCase() as ScheduledEventType) || "report";

      // Hydrate the scheduled-report definition from automation.reportConfig
      // (older automations predate it → defaults). Serialization lives in
      // useAutomationMutations (buildReportConfig) — keep the two in sync.
      const rc = (a as { reportConfig?: Record<string, unknown> }).reportConfig;
      if (rc && typeof rc === "object") {
        state.reportSource =
          rc.source === "favorite" || rc.favoriteViewId != null
            ? "favorite"
            : rc.source === "view" || rc.viewId != null
              ? "view"
              : "new";
        state.reportViewId = rc.viewId != null ? String(rc.viewId) : "";
        state.reportFavoriteViewId =
          rc.favoriteViewId != null ? String(rc.favoriteViewId) : "";
        state.reportTabKey =
          typeof rc.tabKey === "string" && rc.tabKey
            ? rc.tabKey
            : initialState.reportTabKey;
        state.reportFormat = rc.format === "xlsx" ? "xlsx" : "csv";
        state.reportTimeframe =
          typeof rc.timeframe === "string" && rc.timeframe
            ? rc.timeframe
            : initialState.reportTimeframe;
        state.reportSections = Array.isArray(rc.sections)
          ? rc.sections.map(String)
          : [];
        state.reportRecipientType =
          rc.recipientType === "role" || rc.recipientType === "other"
            ? rc.recipientType
            : "user";
        state.reportRecipientId =
          rc.recipientId != null ? String(rc.recipientId) : "";
        state.reportAdditionalEmail =
          typeof rc.additionalEmail === "string" ? rc.additionalEmail : "";
        state.reportNotes = typeof rc.notes === "string" ? rc.notes : "";
        // NOTIFICATION / ALERT configs ride the same column — hydrate their
        // editor fields too (defaults when the keys are absent, i.e. the
        // automation is of another scheduled type).
        state.notificationContext =
          typeof rc.context === "string" && rc.context
            ? rc.context
            : initialState.notificationContext;
        state.notificationMessage =
          a.scheduledType === "NOTIFICATION" && typeof rc.message === "string"
            ? rc.message
            : initialState.notificationMessage;
        state.alertMetric =
          typeof rc.metric === "string" && rc.metric
            ? rc.metric
            : initialState.alertMetric;
        state.alertOperator =
          typeof rc.operator === "string" && rc.operator
            ? rc.operator
            : initialState.alertOperator;
        state.alertThreshold =
          typeof rc.threshold === "number" && Number.isFinite(rc.threshold)
            ? String(rc.threshold)
            : initialState.alertThreshold;
        state.alertMessage =
          a.scheduledType === "ALERT" && typeof rc.message === "string"
            ? rc.message
            : initialState.alertMessage;
        // Email channel: absent key = legacy schedule = email on.
        state.reportEmailEnabled = rc.emailEnabled !== false;
        // Share-link channel: absent key = off (every legacy schedule).
        const share = rc.shareLink as Record<string, unknown> | undefined;
        state.reportShareLinkEnabled = share?.enabled === true;
        state.reportShareLinkNote =
          typeof share?.note === "string" ? share.note : "";
        // WhatsApp delivery block (REPORT schedules).
        const wa = rc.whatsapp as Record<string, unknown> | undefined;
        if (wa && typeof wa === "object") {
          state.reportWhatsappEnabled = true;
          state.reportWhatsappSource =
            typeof wa.provider === "string" && wa.sourceId != null
              ? `${wa.provider}:${wa.sourceId}`
              : "";
          state.reportWhatsappTemplateName =
            typeof wa.templateName === "string" ? wa.templateName : "";
          state.reportWhatsappTemplateLanguage =
            typeof wa.templateLanguage === "string" ? wa.templateLanguage : "";
          state.reportWhatsappParams = Array.isArray(wa.paramMappings)
            ? (wa.paramMappings as any[])
                .filter((m) => m && typeof m.paramName === "string")
                .map((m) => ({
                  paramName: String(m.paramName),
                  source: m.source === "static" ? "static" : "report_value",
                  value: String(m.value ?? ""),
                }))
            : [];
          state.reportWhatsappPhones = Array.isArray(wa.phoneNumbers)
            ? (wa.phoneNumbers as unknown[]).map(String).join(", ")
            : "";
          state.reportWhatsappSendToRecipients = wa.sendToRecipients !== false;
        } else {
          state.reportWhatsappEnabled = initialState.reportWhatsappEnabled;
          state.reportWhatsappSource = initialState.reportWhatsappSource;
          state.reportWhatsappTemplateName =
            initialState.reportWhatsappTemplateName;
          state.reportWhatsappTemplateLanguage =
            initialState.reportWhatsappTemplateLanguage;
          state.reportWhatsappParams = [];
          state.reportWhatsappPhones = initialState.reportWhatsappPhones;
          state.reportWhatsappSendToRecipients =
            initialState.reportWhatsappSendToRecipients;
        }
      } else {
        state.reportSource = initialState.reportSource;
        state.reportViewId = initialState.reportViewId;
        state.reportFavoriteViewId = initialState.reportFavoriteViewId;
        state.reportTabKey = initialState.reportTabKey;
        state.reportFormat = initialState.reportFormat;
        state.reportTimeframe = initialState.reportTimeframe;
        state.reportSections = [];
        state.reportRecipientType = initialState.reportRecipientType;
        state.reportRecipientId = initialState.reportRecipientId;
        state.reportAdditionalEmail = initialState.reportAdditionalEmail;
        state.reportNotes = initialState.reportNotes;
        state.notificationContext = initialState.notificationContext;
        state.notificationMessage = initialState.notificationMessage;
        state.reportEmailEnabled = initialState.reportEmailEnabled;
        state.reportShareLinkEnabled = initialState.reportShareLinkEnabled;
        state.reportShareLinkNote = initialState.reportShareLinkNote;
        state.reportWhatsappEnabled = initialState.reportWhatsappEnabled;
        state.reportWhatsappSource = initialState.reportWhatsappSource;
        state.reportWhatsappTemplateName =
          initialState.reportWhatsappTemplateName;
        state.reportWhatsappTemplateLanguage =
          initialState.reportWhatsappTemplateLanguage;
        state.reportWhatsappParams = [];
        state.reportWhatsappPhones = initialState.reportWhatsappPhones;
        state.reportWhatsappSendToRecipients =
          initialState.reportWhatsappSendToRecipients;
        state.alertMetric = initialState.alertMetric;
        state.alertOperator = initialState.alertOperator;
        state.alertThreshold = initialState.alertThreshold;
        state.alertMessage = initialState.alertMessage;
      }

      if (a.actions && a.actions.length > 0) {
        state.automationActions = a.actions.map((act) => ({
          id: crypto.randomUUID(),
          actionType: act.type,
          config: act.config || {},
        }));
      } else {
        state.automationActions = [
          { id: crypto.randomUUID(), actionType: "", config: {} },
        ];
      }

      state.automationConditionTree = parseConditionPayload(a.conditions);
    },
    setIsAutomationDeleteModal(state, action: PayloadAction<boolean>) {
      state.isDeleteModal = action.payload;
    },
    setAutomationCurrentItem(state, action: PayloadAction<Automation | null>) {
      state.currentItem = action.payload;
    },
    setHandleAutomationCollapseToggle(state) {
      state.isCollapse = !state.isCollapse;
    },
    setAutomationCurrentTab(
      state,
      action: PayloadAction<AutomationState["currentItemTab"]>
    ) {
      state.currentItemTab = action.payload;
    },
    setToggleAddAutomation(state) {
      state.isAddAutomation = !state.isAddAutomation;
    },
    /**
     * Xale AI is about to stream a drafted rule into the builder: open the
     * event-based builder (idempotent — `setToggleAddAutomation` would CLOSE an
     * already-open one). A new rule starts from a clean form; an edit keeps
     * the form `populateFormFromAutomation` just hydrated, so the dials the
     * draft does not carry (group, skipIfAlreadyApplied) survive the save.
     */
    beginAutomationDraft(state, action: PayloadAction<{ keepForm: boolean }>) {
      if (!action.payload.keepForm) {
        automationFunctionalSlice.caseReducers.resetAutomationForm(state);
      }
      state.automationType = "event-based";
      state.isAddAutomation = true;
      state.isDetailDrawerOpen = false;
    },
    setToggleEditAutomation(state) {
      state.isEditAutomation = !state.isEditAutomation;
    },
    setAutomationSearch(state, action: PayloadAction<string>) {
      state.automationSearch = action.payload;
    },
    setIsDetailDrawerOpen(state, action: PayloadAction<boolean>) {
      state.isDetailDrawerOpen = action.payload;
    },
    setIsLogDetailDrawerOpen(state, action: PayloadAction<boolean>) {
      state.isLogDetailDrawerOpen = action.payload;
    },
    setSelectedLog(state, action: PayloadAction<any>) {
      state.selectedLog = action.payload;
    },
    setIsBatchDrawerOpen(state, action: PayloadAction<boolean>) {
      state.isBatchDrawerOpen = action.payload;
    },
    setSelectedBatch(state, action: PayloadAction<any>) {
      state.selectedBatch = action.payload;
    },
    setAutomationType(state, action: PayloadAction<AutomationType>) {
      state.automationType = action.payload;
    },
    setAutomationName(state, action: PayloadAction<string>) {
      state.automationName = action.payload;
    },
    setAutomationDescription(state, action: PayloadAction<string>) {
      state.automationDescription = action.payload;
    },
    setAutomationTrigger(state, action: PayloadAction<string | null>) {
      state.automationTrigger = action.payload;
    },
    setAutomationTriggerConfig(
      state,
      action: PayloadAction<Record<string, any>>
    ) {
      state.automationTriggerConfig = action.payload;
    },
    setAutomationScope(state, action: PayloadAction<AutomationScopeForm>) {
      state.automationScope = action.payload;
    },
    setAutomationGroupId(state, action: PayloadAction<number | null>) {
      state.automationGroupId = action.payload;
    },
    setAutomationFrequency(
      state,
      action: PayloadAction<AutomationFrequencyForm>
    ) {
      state.automationFrequency = action.payload;
    },
    setAutomationPeriodSeconds(state, action: PayloadAction<number>) {
      state.automationPeriodSeconds = action.payload;
    },
    setRunWindowMode(state, action: PayloadAction<RunWindowModeForm>) {
      state.runWindowMode = action.payload;
    },
    setRunWindowDays(state, action: PayloadAction<number[]>) {
      state.runWindowDays = [...new Set(action.payload)]
        .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
        .sort((a, b) => a - b);
    },
    toggleRunWindowDay(state, action: PayloadAction<number>) {
      const day = action.payload;
      state.runWindowDays = state.runWindowDays.includes(day)
        ? state.runWindowDays.filter((d) => d !== day)
        : [...state.runWindowDays, day].sort((a, b) => a - b);
    },
    setRunWindowStart(state, action: PayloadAction<number>) {
      state.runWindowStart = action.payload;
    },
    setRunWindowEnd(state, action: PayloadAction<number>) {
      state.runWindowEnd = action.payload;
    },
    setStopOnFailure(state, action: PayloadAction<boolean>) {
      state.stopOnFailure = action.payload;
    },
    setSkipIfAlreadyApplied(state, action: PayloadAction<boolean>) {
      state.skipIfAlreadyApplied = action.payload;
    },
    setAutomationPriority(state, action: PayloadAction<number>) {
      state.automationPriority = action.payload;
    },
    setBranchId(state, action: PayloadAction<number | null>) {
      state.branchId = action.payload;
    },
    setCronExpression(state, action: PayloadAction<string>) {
      state.cronExpression = action.payload;
    },

    // ── Scheduled NOTIFICATION / ALERT reducers ──

    setNotificationContext(state, action: PayloadAction<string>) {
      state.notificationContext = action.payload;
    },
    setNotificationMessage(state, action: PayloadAction<string>) {
      state.notificationMessage = action.payload;
    },
    setAlertMetric(state, action: PayloadAction<string>) {
      state.alertMetric = action.payload;
    },
    setAlertOperator(state, action: PayloadAction<string>) {
      state.alertOperator = action.payload;
    },
    setAlertThreshold(state, action: PayloadAction<string>) {
      state.alertThreshold = action.payload;
    },
    setAlertMessage(state, action: PayloadAction<string>) {
      state.alertMessage = action.payload;
    },

    // ── Scheduled-report WhatsApp delivery reducers ──

    setReportEmailEnabled(state, action: PayloadAction<boolean>) {
      state.reportEmailEnabled = action.payload;
    },
    setReportShareLinkEnabled(state, action: PayloadAction<boolean>) {
      state.reportShareLinkEnabled = action.payload;
    },
    setReportShareLinkNote(state, action: PayloadAction<string>) {
      state.reportShareLinkNote = action.payload;
    },
    setReportWhatsappEnabled(state, action: PayloadAction<boolean>) {
      state.reportWhatsappEnabled = action.payload;
    },
    setReportWhatsappSource(state, action: PayloadAction<string>) {
      state.reportWhatsappSource = action.payload;
      // A template belongs to ONE account — switching accounts invalidates it.
      state.reportWhatsappTemplateName = "";
      state.reportWhatsappTemplateLanguage = "";
      state.reportWhatsappParams = [];
    },
    setReportWhatsappTemplate(
      state,
      action: PayloadAction<{
        name: string;
        language?: string;
        /** The template's variable names — seeds one mapping row per param. */
        params: string[];
      }>
    ) {
      state.reportWhatsappTemplateName = action.payload.name;
      state.reportWhatsappTemplateLanguage = action.payload.language ?? "";
      // Default every variable to the report's headline total — per the
      // operators, "the total number is the most used one".
      state.reportWhatsappParams = action.payload.params.map((paramName) => ({
        paramName,
        source: "report_value" as const,
        value: "new_leads",
      }));
    },
    updateReportWhatsappParam(
      state,
      action: PayloadAction<{ index: number; patch: Partial<ReportWhatsappParamForm> }>
    ) {
      const row = state.reportWhatsappParams[action.payload.index];
      if (row) Object.assign(row, action.payload.patch);
    },
    setReportWhatsappPhones(state, action: PayloadAction<string>) {
      state.reportWhatsappPhones = action.payload;
    },
    setReportWhatsappSendToRecipients(state, action: PayloadAction<boolean>) {
      state.reportWhatsappSendToRecipients = action.payload;
    },

    // ── Condition tree reducers ──

    setConditionTree(state, action: PayloadAction<ConditionGroupForm>) {
      state.automationConditionTree = action.payload;
    },
    addConditionLeafTo(
      state,
      action: PayloadAction<{ groupId: string }>
    ) {
      const group = findGroup(
        state.automationConditionTree as ConditionGroupForm,
        action.payload.groupId,
      );
      if (group) group.children.push(newLeaf());
    },
    addConditionGroupTo(
      state,
      action: PayloadAction<{
        parentId: string;
        combinator?: ConditionCombinator;
      }>
    ) {
      const parent = findGroup(
        state.automationConditionTree as ConditionGroupForm,
        action.payload.parentId,
      );
      if (parent) {
        parent.children.push(newGroup(action.payload.combinator || "AND", [newLeaf()]));
      }
    },
    updateConditionLeaf(
      state,
      action: PayloadAction<{
        id: string;
        field: keyof Pick<ConditionLeafForm, "field" | "operator" | "value">;
        value: string;
      }>
    ) {
      const leaf = findLeaf(
        state.automationConditionTree as ConditionGroupForm,
        action.payload.id,
      );
      if (leaf) {
        leaf[action.payload.field] = action.payload.value;
        // When the field changes, clear value so stale selections from the
        // previous field's option list don't get serialized.
        if (action.payload.field === "field") {
          leaf.value = "";
          delete leaf.matchCase;
        }
      }
    },
    setConditionLeafMatchCase(
      state,
      action: PayloadAction<{ id: string; matchCase: boolean }>
    ) {
      const leaf = findLeaf(
        state.automationConditionTree as ConditionGroupForm,
        action.payload.id,
      );
      if (leaf) {
        if (action.payload.matchCase) leaf.matchCase = true;
        else delete leaf.matchCase;
      }
    },
    setGroupCombinator(
      state,
      action: PayloadAction<{ id: string; combinator: ConditionCombinator }>
    ) {
      const group = findGroup(
        state.automationConditionTree as ConditionGroupForm,
        action.payload.id,
      );
      if (group) group.combinator = action.payload.combinator;
    },
    removeConditionNode(state, action: PayloadAction<{ id: string }>) {
      const root = state.automationConditionTree as ConditionGroupForm;
      // Cannot remove the root.
      if (root.id === action.payload.id) return;
      const result = findNode(root, action.payload.id);
      if (!result || !result.parent) return;
      result.parent.children = result.parent.children.filter(
        (c) => c.id !== action.payload.id,
      );
      // Don't leave the root completely empty - keep one starter leaf.
      if (root.children.length === 0) root.children.push(newLeaf());
    },

    /**
     * Wholesale replace, the actions twin of `setConditionTree`. Used by the
     * Xale AI drafter, which lands a whole ordered action list in one go —
     * the per-action setters can't (`addAutomationAction` hands back no id and
     * setting `actionType` wipes `config`). Never leaves the list empty.
     */
    setAutomationActions(
      state,
      action: PayloadAction<{ actionType: string; config?: Record<string, any> }[]>
    ) {
      const next = action.payload.map((a) => ({
        id: crypto.randomUUID(),
        actionType: a.actionType,
        config: a.config ?? {},
      }));
      state.automationActions = next.length
        ? next
        : [{ id: crypto.randomUUID(), actionType: "", config: {} }];
    },
    addAutomationAction(state) {
      state.automationActions.push({
        id: crypto.randomUUID(),
        actionType: "",
        config: {},
      });
    },
    updateAutomationAction(
      state,
      action: PayloadAction<{
        id: string;
        field: keyof AutomationActionForm;
        value: any;
      }>
    ) {
      const act = state.automationActions.find(
        (a) => a.id === action.payload.id
      );
      if (act) {
        (act as any)[action.payload.field] = action.payload.value;
        if (action.payload.field === "actionType") {
          act.config = {};
        }
      }
    },
    updateAutomationActionConfig(
      state,
      action: PayloadAction<{ id: string; key: string; value: any }>
    ) {
      const act = state.automationActions.find(
        (a) => a.id === action.payload.id
      );
      if (act) {
        act.config = {
          ...act.config,
          [action.payload.key]: action.payload.value,
        };
      }
    },
    removeAutomationAction(state, action: PayloadAction<string>) {
      state.automationActions = state.automationActions.filter(
        (a) => a.id !== action.payload
      );
    },
    /**
     * Drag-reorder the action list. ARRAY ORDER IS EXECUTION ORDER: the save
     * payload sends the list as-is and the server stamps `AutomationAction.
     * order` from the index (`automation.repo.ts`), reading it back
     * `orderBy: { order: "asc" }`. So moving a card here really does change
     * which action runs first — that is the whole point (e.g. set the stage
     * BEFORE shuffling, so the shuffle sees the new stage).
     */
    reorderAutomationActions(
      state,
      action: PayloadAction<{ from: number; to: number }>
    ) {
      const { from, to } = action.payload;
      const list = state.automationActions;
      if (
        from === to ||
        from < 0 ||
        to < 0 ||
        from >= list.length ||
        to >= list.length
      ) {
        return;
      }
      const [moved] = list.splice(from, 1);
      if (moved) list.splice(to, 0, moved);
    },
    setScheduledEventType(state, action: PayloadAction<ScheduledEventType>) {
      state.scheduledEventType = action.payload;
    },
    setReportMetric(state, action: PayloadAction<string>) {
      state.reportMetric = action.payload;
    },
    setReportGroupBy(state, action: PayloadAction<string>) {
      state.reportGroupBy = action.payload;
    },
    addReportFilter(state) {
      state.reportFilters.push({
        id: crypto.randomUUID(),
        field: "",
        operator: "",
        value: "",
      });
    },
    updateReportFilter(
      state,
      action: PayloadAction<{
        id: string;
        field: keyof AutomationCondition;
        value: string;
      }>
    ) {
      const filter = state.reportFilters.find(
        (f) => f.id === action.payload.id
      );
      if (filter) {
        filter[action.payload.field] = action.payload.value;
      }
    },
    removeReportFilter(state, action: PayloadAction<string>) {
      state.reportFilters = state.reportFilters.filter(
        (f) => f.id !== action.payload
      );
    },
    /** Picking a FAVORITE (saved leads) view — the numbers-only source. */
    setReportFavoriteView(state, action: PayloadAction<string>) {
      state.reportSource = "favorite";
      state.reportFavoriteViewId = action.payload;
      state.reportViewId = "";
    },
    /** Picking a saved view also mirrors its tab (sections checklist follows). */
    setReportView(
      state,
      action: PayloadAction<{ viewId: string; tabKey?: string }>
    ) {
      state.reportSource = "view";
      state.reportViewId = action.payload.viewId;
      if (action.payload.tabKey && action.payload.tabKey !== state.reportTabKey) {
        state.reportTabKey = action.payload.tabKey;
        state.reportSections = [];
      }
    },
    /** "Create new" — picking a tab switches source and resets sections. */
    setReportTabKey(state, action: PayloadAction<string>) {
      state.reportSource = "new";
      state.reportViewId = "";
      if (action.payload !== state.reportTabKey) {
        state.reportTabKey = action.payload;
        state.reportSections = [];
      }
    },
    setReportFormat(state, action: PayloadAction<ReportExportFormat>) {
      state.reportFormat = action.payload;
    },
    setReportTimeframe(state, action: PayloadAction<string>) {
      state.reportTimeframe = action.payload;
    },
    toggleReportSection(state, action: PayloadAction<string>) {
      state.reportSections = state.reportSections.includes(action.payload)
        ? state.reportSections.filter((s) => s !== action.payload)
        : [...state.reportSections, action.payload];
    },
    setReportRecipientType(state, action: PayloadAction<ReportRecipientType>) {
      state.reportRecipientType = action.payload;
    },
    setReportRecipientId(state, action: PayloadAction<string>) {
      state.reportRecipientId = action.payload;
    },
    setReportAdditionalEmail(state, action: PayloadAction<string>) {
      state.reportAdditionalEmail = action.payload;
    },
    setReportNotes(state, action: PayloadAction<string>) {
      state.reportNotes = action.payload;
    },
  },
});

export const {
  resetAutomationForm,
  populateFormFromAutomation,
  setIsAutomationDeleteModal,
  setAutomationCurrentItem,
  setHandleAutomationCollapseToggle,
  setAutomationCurrentTab,
  setToggleEditAutomation,
  setToggleAddAutomation,
  beginAutomationDraft,
  setAutomationSearch,
  setIsDetailDrawerOpen,
  setIsLogDetailDrawerOpen,
  setSelectedLog,
  setIsBatchDrawerOpen,
  setSelectedBatch,
  setAutomationType,
  setAutomationName,
  setAutomationDescription,
  setAutomationTrigger,
  setAutomationTriggerConfig,
  setAutomationScope,
  setAutomationGroupId,
  setAutomationFrequency,
  setAutomationPeriodSeconds,
  setRunWindowMode,
  toggleRunWindowDay,
  setRunWindowDays,
  setRunWindowStart,
  setRunWindowEnd,
  setStopOnFailure,
  setSkipIfAlreadyApplied,
  setAutomationPriority,
  setBranchId,
  setCronExpression,
  setNotificationContext,
  setNotificationMessage,
  setReportEmailEnabled,
  setReportShareLinkEnabled,
  setReportShareLinkNote,
  setReportWhatsappEnabled,
  setReportWhatsappSource,
  setReportWhatsappTemplate,
  updateReportWhatsappParam,
  setReportWhatsappPhones,
  setReportWhatsappSendToRecipients,
  setAlertMetric,
  setAlertOperator,
  setAlertThreshold,
  setAlertMessage,
  setConditionTree,
  addConditionLeafTo,
  addConditionGroupTo,
  updateConditionLeaf,
  setConditionLeafMatchCase,
  setGroupCombinator,
  removeConditionNode,
  addAutomationAction,
  updateAutomationAction,
  updateAutomationActionConfig,
  reorderAutomationActions,
  setAutomationActions,
  removeAutomationAction,
  setScheduledEventType,
  setReportMetric,
  setReportGroupBy,
  addReportFilter,
  updateReportFilter,
  removeReportFilter,
  setReportFavoriteView,
  setReportView,
  setReportTabKey,
  setReportFormat,
  setReportTimeframe,
  toggleReportSection,
  setReportRecipientType,
  setReportRecipientId,
  setReportAdditionalEmail,
  setReportNotes,
} = automationFunctionalSlice.actions;
export default automationFunctionalSlice.reducer;
